import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';

/** Animação de fundo (canvas) com estrelas e estrelas cadentes. */
@Component({
    selector: 'app-stars-animation',
    standalone: true,
    imports: [],
    templateUrl: './stars-animation.html',
    styleUrl: './stars-animation.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StarsAnimation implements AfterViewInit, OnDestroy {
    private canvas!: HTMLCanvasElement;
    private ctx!: CanvasRenderingContext2D;
    private animationId!: number;
    private estrelas: ReturnType<StarsAnimation['criarEstrela']>[] = [];
    private estrelasCadentes: ReturnType<StarsAnimation['criarEstrelaCadente']>[] = [];
    private quantidadeEstrelas = 200;
    private coresEstrelas = ['#ffffff', '#bfdbfe', '#fef08a', '#e2e8f0'];

    ngAfterViewInit() {
        this.canvas = document.getElementById('canvas-fundo-stars') as HTMLCanvasElement;
        if (!this.canvas) return;
        this.ctx = this.canvas.getContext('2d')!;
        this.iniciar();
        window.addEventListener('resize', this.redimensionarCanvas.bind(this));
    }

    ngOnDestroy() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
        window.removeEventListener('resize', this.redimensionarCanvas.bind(this));
    }

    private redimensionarCanvas() {
        if (this.canvas) {
            this.canvas.width = window.innerWidth;
            this.canvas.height = window.innerHeight;
        }
    }

    private iniciar() {
        this.redimensionarCanvas();
        for (let i = 0; i < this.quantidadeEstrelas; i++) {
            this.estrelas.push(this.criarEstrela());
        }
        for (let i = 0; i < 3; i++) {
            this.estrelasCadentes.push(this.criarEstrelaCadente());
        }
        this.animar();
    }

    private criarEstrela() {
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * window.innerHeight;
        const tamanho = Math.random() * 2 + 0.5;
        return {
            x,
            y,
            tamanho,
            velocidadeX: -(tamanho * 0.15 + 0.05),
            cor: this.coresEstrelas[Math.floor(Math.random() * this.coresEstrelas.length)],
            faseBrilho: Math.random() * Math.PI * 2,
            velocidadeBrilho: Math.random() * 0.03 + 0.01,
            opacidadeBase: Math.random() * 0.5 + 0.3,
            atualizar: function (canvasWidth: number) {
                this.x += this.velocidadeX;
                this.faseBrilho += this.velocidadeBrilho;
                if (this.x < -this.tamanho) {
                    this.x = canvasWidth + this.tamanho;
                    this.y = Math.random() * window.innerHeight;
                }
            },
            desenhar(ctx: CanvasRenderingContext2D) {
                ctx.save();
                const brilhoAtual = this.opacidadeBase + Math.sin(this.faseBrilho) * 0.3;
                ctx.globalAlpha = Math.max(0, Math.min(1, brilhoAtual));
                ctx.fillStyle = this.cor;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.tamanho, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            }
        };
    }

    private criarEstrelaCadente() {
        return {
            ativo: false,
            x: 0,
            y: 0,
            tamanho: 0,
            velocidadeX: 0,
            velocidadeY: 0,
            comprimento: 0,
            opacidade: 0,
            resetar: function (canvasWidth: number, canvasHeight: number) {
                this.ativo = false;
                this.x = Math.random() * canvasWidth * 1.5;
                this.y = Math.random() * canvasHeight * 0.5 - 200;
                this.tamanho = Math.random() * 1.5 + 1;
                this.velocidadeX = -(Math.random() * 8 + 6);
                this.velocidadeY = Math.random() * 3 + 2;
                this.comprimento = Math.random() * 100 + 50;
                this.opacidade = 0;
            },
            atualizar: function (canvasWidth: number, canvasHeight: number) {
                if (!this.ativo) {
                    if (Math.random() < 0.003) {
                        this.ativo = true;
                        this.opacidade = 1;
                    }
                    return;
                }
                this.x += this.velocidadeX;
                this.y += this.velocidadeY;
                this.opacidade -= 0.015;
                if (this.opacidade <= 0 || this.y > canvasHeight || this.x < -this.comprimento) {
                    this.resetar(canvasWidth, canvasHeight);
                }
            },
            desenhar(ctx: CanvasRenderingContext2D) {
                if (!this.ativo) return;
                ctx.save();
                ctx.globalAlpha = Math.max(0, this.opacidade);
                const gradiente = ctx.createLinearGradient(this.x, this.y, this.x - this.velocidadeX * 10, this.y - this.velocidadeY * 10);
                gradiente.addColorStop(0, 'rgba(255, 255, 255, 1)');
                gradiente.addColorStop(1, 'rgba(255, 255, 255, 0)');
                ctx.beginPath();
                ctx.strokeStyle = gradiente;
                ctx.lineWidth = this.tamanho;
                ctx.moveTo(this.x - this.velocidadeX * 10, this.y - this.velocidadeY * 10);
                ctx.lineTo(this.x, this.y);
                ctx.stroke();
                ctx.restore();
            }
        };
    }

    private animar() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.estrelas.forEach((estrela) => {
            estrela.atualizar(this.canvas.width);
            estrela.desenhar(this.ctx);
        });
        this.estrelasCadentes.forEach((cadente) => {
            cadente.atualizar(this.canvas.width, this.canvas.height);
            cadente.desenhar(this.ctx);
        });
        this.animationId = requestAnimationFrame(this.animar.bind(this));
    }
}
